import React, {useContext, useMemo, useState} from "react";
import "./Calendar.scss";
import CalendarMonth from "./CalendarMonth";
import {useFetchDatesCalendar} from "../../../service/Business/CalendarService";
import FCSpinner from "../../FCObjets/FCSpinner";
import {EventUpdatedContext} from "../../../store/EventUpdatedContext";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import {EnumTypeEvent, EventsInterface, PeriodInterface} from "../../../service/Business/Interfaces/CalendarInterface";
import FCContentModalFL, {DataForm} from "../../FCObjets/Form/FCContentModalFL";
import {TypeForm} from "../../../types/EnumTypeForm";
import FCYearNav from "../../FCObjets/FCYearNav";
import {AppSettings} from "../../../AppSettings";

type ContentCalendarProps = {
    editable: boolean;
    username: string;
};

type PlanningView = "year" | "quarter" | "month";

const COLORS = AppSettings.Freelancerie.Calendar;

const ContentCalendar: React.FunctionComponent<ContentCalendarProps> = (props) => {

    const {cptUpdated} = useContext(EventUpdatedContext);
    const [year, setYear] = useState(new Date().getFullYear());
    const [view, setView] = useState<PlanningView>("year");
    const {data: dates, loading, error} = useFetchDatesCalendar(props.username, year, !props.editable, cptUpdated);

    const [data, setData] = useState<DataForm>({
        type: TypeForm.EMPTY,
        data: undefined,
    });

    const gestionModal = (open: boolean, e: PeriodInterface | undefined, type: TypeForm | undefined) => {
        setData({type: (open && type) ? type : TypeForm.EMPTY, data: e});
    };

    const clickEventDay = (dataPeriod: PeriodInterface): void => {
        gestionModal(true, dataPeriod, TypeForm.PERIOD);
    };

    /**
     * Calcule le nombre de jours disponibles dans l'année (hors weekends/vacances/fériés).
     */
    const disponibilite = useMemo(() => {
        if (!dates) return 0;
        const events = dates.listEvents || [];
        const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        const totalDays = isLeap ? 366 : 365;

        let unavailable = 0;
        // Weekends
        for (let m = 0; m < 12; m++) {
            const daysInMonth = new Date(year, m + 1, 0).getDate();
            for (let d = 1; d <= daysInMonth; d++) {
                const day = new Date(year, m, d).getDay();
                if (day === 0 || day === 6) unavailable++;
            }
        }
        // Jours off (vacances + fériés)
        const countedDates = new Set<string>();
        for (const ev of events) {
            if (ev.type === EnumTypeEvent.HOLIDAY || ev.type === EnumTypeEvent.FERIE) {
                const begin = new Date(ev.begin);
                const end = new Date(ev.end);
                const cursor = new Date(begin);
                while (cursor <= end) {
                    const key = cursor.toISOString().slice(0, 10);
                    const d = cursor.getDay();
                    if (d !== 0 && d !== 6 && !countedDates.has(key)) {
                        countedDates.add(key);
                        unavailable++;
                    }
                    cursor.setDate(cursor.getDate() + 1);
                }
            }
        }
        return Math.max(0, totalDays - unavailable);
    }, [dates, year]);

    /**
     * Filtre les mois affichés selon la vue sélectionnée.
     */
    const monthsToShow = useMemo((): number[] => {
        if (view === "month") {
            return [new Date().getMonth() + 1];
        }
        if (view === "quarter") {
            const currentQuarter = Math.floor(new Date().getMonth() / 3);
            return [1, 2, 3].map((i) => currentQuarter * 3 + i);
        }
        return Array.from({length: 12}, (_, i) => i + 1);
    }, [view]);

    /**
     * Événements à venir (triés par date, prochains 6).
     */
    const upcomingEvents = useMemo((): EventsInterface[] => {
        if (!dates?.listEvents) return [];
        const now = new Date();
        return [...dates.listEvents]
            .filter((ev) => ev.type !== EnumTypeEvent.WEEKEND && new Date(ev.end) >= now)
            .sort((a, b) => new Date(a.begin).getTime() - new Date(b.begin).getTime())
            .slice(0, 6);
    }, [dates]);

    /**
     * Couleur et icône de bordure selon le type d'événement.
     */
    const getEventVisuals = (type: EnumTypeEvent): {color: string; icon: string} => {
        switch (type) {
            case EnumTypeEvent.HOLIDAY:
                return {color: COLORS.COLOR_HOLIDAY, icon: "beach_access"};
            case EnumTypeEvent.FERIE:
                return {color: COLORS.COLOR_FERIE, icon: "celebration"};
            case EnumTypeEvent.WORKED:
                return {color: COLORS.COLOR_WORKED, icon: "work"};
            case EnumTypeEvent.URSSAF:
                return {color: COLORS.COLOR_URSSAF, icon: "account_balance"};
            case EnumTypeEvent.CRITIC:
                return {color: COLORS.COLOR_CRITIC, icon: "warning"};
            default:
                return {color: "#7F8C8D", icon: "event"};
        }
    };

    const formatEventDate = (begin: string, end: string): string => {
        const b = new Date(begin);
        const e = new Date(end);
        const opts: Intl.DateTimeFormatOptions = {day: "2-digit", month: "short"};
        const sameDay = b.toDateString() === e.toDateString();
        return sameDay
            ? b.toLocaleDateString("fr-FR", opts).toUpperCase()
            : `${b.toLocaleDateString("fr-FR", opts)} - ${e.toLocaleDateString("fr-FR", opts)}`.toUpperCase();
    };

    return (
        <FCContentModalFL form={data} onModalFormFinished={() => gestionModal(false, undefined, undefined)}>
            <div className="calendarPage">
                {/* Header */}
                <div className="calendarHeader">
                    <div>
                        <h1 className="calendarTitle">Planning Annuel {year}</h1>
                        <p className="calendarSubtitle">Gérez votre capacité, vos échéances fiscales et vos temps de repos.</p>
                    </div>
                    <div className="calendarHeaderRight">
                        <div className="calendarViewSwitch">
                            <button className={`calendarViewBtn${view === "year" ? " active" : ""}`}
                                    onClick={() => setView("year")}>Année</button>
                            <button className={`calendarViewBtn${view === "quarter" ? " active" : ""}`}
                                    onClick={() => setView("quarter")}>Trimestre</button>
                            <button className={`calendarViewBtn${view === "month" ? " active" : ""}`}
                                    onClick={() => setView("month")}>Mois</button>
                        </div>
                        <FCYearNav year={year} onChange={setYear}/>
                        {props.editable && (
                            <button className="calendarAddBtn"
                                    onClick={() => gestionModal(true, undefined, TypeForm.PERIOD)}>
                                <span className="material-symbols-outlined">add</span>
                                Ajouter
                            </button>
                        )}
                    </div>
                </div>

                {/* Legend + Disponibilité */}
                <div className="calendarLegendRow">
                    <div className="calendarLegend">
                        <div className="calendarLegendItem">
                            <span className="calendarLegendDot" style={{background: COLORS.COLOR_HOLIDAY}}/>
                            Vacances
                        </div>
                        <div className="calendarLegendItem">
                            <span className="calendarLegendDot" style={{background: COLORS.COLOR_FERIE}}/>
                            Jours fériés
                        </div>
                        <div className="calendarLegendItem">
                            <span className="calendarLegendDot" style={{background: COLORS.COLOR_WORKED}}/>
                            Contrats / Jours travaillés
                        </div>
                        <div className="calendarLegendItem">
                            <span className="calendarLegendDot" style={{background: COLORS.COLOR_URSSAF}}/>
                            Échéances URSSAF
                        </div>
                        <div className="calendarLegendItem">
                            <span className="calendarLegendDot" style={{background: COLORS.COLOR_CRITIC}}/>
                            Alerte revenu max
                        </div>
                        <div className="calendarLegendItem">
                            <span className="calendarLegendDot" style={{background: COLORS.COLOR_WEEKEND}}/>
                            Weekends
                        </div>
                    </div>
                    <div className="calendarDisponibilite">
                        <div className="calendarDispoLabel">Disponibilité</div>
                        <div className="calendarDispoValue">{disponibilite} <span>jours / an</span></div>
                    </div>
                </div>

                {loading && <div className="calendarLoading"><FCSpinner size="small"/></div>}
                {error && <FCAlertMsgErrorAPI error={error}/>}

                {dates && (
                    <>
                        <div className={`calendarGrid view-${view}`}>
                            {monthsToShow.map((m) => (
                                <CalendarMonth key={m} month={m} year={year} data={dates}
                                               editable={props.editable} onClickEventDay={clickEventDay}/>
                            ))}
                        </div>

                        {/* Détails des Événements */}
                        {upcomingEvents.length > 0 && (
                            <div className="calendarEventsSection">
                                <h2 className="calendarEventsTitle">Détails des Événements</h2>
                                <div className="calendarEventsGrid">
                                    {upcomingEvents.map((ev, idx) => {
                                        const v = getEventVisuals(ev.type);
                                        return (
                                            <div className="calendarEventCard" key={idx}
                                                 style={{borderLeftColor: v.color}}>
                                                <div className="calendarEventHead">
                                                    <span className="material-symbols-outlined calendarEventIcon"
                                                          style={{color: v.color}}>{v.icon}</span>
                                                    <span className="calendarEventDate">{formatEventDate(ev.begin, ev.end)}</span>
                                                </div>
                                                <div className="calendarEventTitleText">{ev.title}</div>
                                                {ev.description && (
                                                    <div className="calendarEventDesc">{ev.description}</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </FCContentModalFL>
    );
};

export default ContentCalendar;
